import React, { useEffect, useState } from "react";
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "../../App";
import { formatDateVN, formatDateVNDate } from '../../utils/formatDate';
import "./lessonLibrary.css";

export default function LessonLibrary() {
  const { user } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL;
  const resolveThumbnailSrc = (thumb) => {
    if (!thumb) return '';
    try {
      if (String(thumb).startsWith('data:')) return thumb;
      if (String(thumb).startsWith('http://') || String(thumb).startsWith('https://')) return thumb;
      if (String(thumb).startsWith('/')) {
        const base = apiUrl ? apiUrl.replace(/\/$/, '') : '';
        return base + thumb;
      }
      return thumb;
    } catch (e) { return thumb; }
  };

  const [grades, setGrades] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [selectedGradeId, setSelectedGradeId] = useState(null);
  const [lessonsForGrade, setLessonsForGrade] = useState([]);


  const [selectedLessonId, setSelectedLessonId] = useState(null);
  const [selectedWeekIdx, setSelectedWeekIdx] = useState(0);
  const [attendanceByWeek, setAttendanceByWeek] = useState({});
  const [attendanceExistsByWeek, setAttendanceExistsByWeek] = useState({});
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 900 : false);
  const [hasOwnClass, setHasOwnClass] = useState(null); // null=unknown, true/false

  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth <= 900); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // load grades and lessons
  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const token = localStorage.getItem('token');
        if (!apiUrl || !token) return;
        const [gres, lres] = await Promise.all([
          fetch(`${apiUrl}/api/grades`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${apiUrl}/api/lessons`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        if (gres.ok) {
          const gd = await gres.json();
          if (mounted) setGrades(gd.grades || []);
        }
        if (lres.ok) {
          const ld = await lres.json();
          if (mounted) setLessons(ld.lessons || []);
        }
      } catch (e) { console.error(e); }
    }
    load();
    return () => { mounted = false; };
  }, [apiUrl]);
  // determine default grade per role
  useEffect(() => {
    if (!user) return;
    const role = (user.role || '').toLowerCase();
    const token = localStorage.getItem('token');
    if (!apiUrl || !token) return;

    if (role === 'mod') {
      if (grades && grades.length) setSelectedGradeId(grades[0].id);
      return;
    }

    if (role === 'teacher') {
      (async () => {
        try {
          const cres = await fetch(`${apiUrl}/api/classes`, { headers: { Authorization: `Bearer ${token}` } });
          if (!cres.ok) return;
          const cd = await cres.json();
          const myClass = (cd.classes || []).find(c => c.teacher_username === user.username) || null;
          setHasOwnClass(!!myClass);
          if (myClass) setSelectedGradeId(myClass.grade_id);
        } catch (e) { console.error(e); }
      })();
      return;
    }

    if (role === 'student') {
      (async () => {
        try {
          if (!user || !user.class_id) { setHasOwnClass(false); return; }
          const cres = await fetch(`${apiUrl}/api/classes`, { headers: { Authorization: `Bearer ${token}` } });
          if (!cres.ok) return;
          const cd = await cres.json();
          const myClass = (cd.classes || []).find(c => String(c.id) === String(user.class_id)) || null;
          setHasOwnClass(!!myClass);
          if (myClass && myClass.grade_id) setSelectedGradeId(myClass.grade_id);
        } catch (e) { console.error(e); }
      })();
      return;
    }
  }, [apiUrl, user, grades]);

  // when grade selected, compute lessons list for that grade and reset selection
  useEffect(() => {
    if (!selectedGradeId) {
      setLessonsForGrade([]);
      setSelectedLessonId(null);
      setSelectedWeekIdx(0);
      return;
    }
    const list = (lessons || []).filter(l => Number(l.grade_id) === Number(selectedGradeId));
    setLessonsForGrade(list);
    if (list.length) {
      setSelectedLessonId(prev => prev || list[0].id);
    } else {
      setSelectedLessonId(null);
    }
    setSelectedWeekIdx(0);
  }, [selectedGradeId, lessons]);

  // no-op: parent-specific behavior removed; Students default to their class grade earlier

  const selectedLesson = lessonsForGrade.find(l => l.id === selectedLessonId) || null;

  // Build unified weekNumbers list from content and activities so buttons and selection are consistent
  const weekNumbers = (() => {
    if (!selectedLesson) return [];
    // If the entire lesson is marked inactive, treat it as having no active weeks
    if (selectedLesson.status === 'inactive') return [];
    const contentWeeks = Array.isArray(selectedLesson.content) ? selectedLesson.content : [];
    // normalize activities: could be array, or stringified JSON, or stored under different prop names
    const rawActivities = selectedLesson.activities || selectedLesson.activity || selectedLesson.activity_list || selectedLesson.activity_json || null;
    let activityWeeks = [];
    if (Array.isArray(rawActivities)) activityWeeks = rawActivities;
    else if (typeof rawActivities === 'string' && rawActivities.trim()) {
      try { activityWeeks = JSON.parse(rawActivities); } catch (e) { /* not JSON, ignore */ }
    }
    const nums = new Set();
    // from content: use explicit week if present else implicit (index+1)
    for (let i = 0; i < contentWeeks.length; i++) {
      const cw = contentWeeks[i];
      // treat missing status as active; if the lesson itself is inactive treat as inactive
      const itemStatus = (cw && (cw.status === undefined || cw.status === null)) ? 'active' : (cw && cw.status ? cw.status : 'active');
      const status = (selectedLesson.status === 'inactive') ? 'inactive' : itemStatus;
      if (status === 'inactive') continue;
      const n = (cw && (cw.week !== undefined && cw.week !== null)) ? Number(cw.week) : (i + 1);
      if (n) nums.add(Number(n));
    }
    // from activities: use their week numbers (respect status flag if present)
    for (const a of activityWeeks) {
      const itemStatus = (a && (a.status === undefined || a.status === null)) ? 'active' : (a && a.status ? a.status : 'active');
      const status = (selectedLesson.status === 'inactive') ? 'inactive' : itemStatus;
      if (status === 'inactive') continue;
      if (a && a.week !== undefined && a.week !== null) nums.add(Number(a.week));
    }
    // if nothing found, default to a single week 1 when there is any content/activity
    if (!nums.size && (contentWeeks.length || activityWeeks.length)) nums.add(1);
    const arr = Array.from(nums).sort((a, b) => a - b);
    return arr;
  })();

  // verbose debug: log lesson shapes when selection changes so we can inspect activity storage
  useEffect(() => {
    if (!selectedLesson) return;
    try {
      const rawActivities = selectedLesson.activities || selectedLesson.activity || selectedLesson.activity_list || selectedLesson.activity_json || null;
      console.debug('[LessonLibrary DEBUG] selectedLesson raw:', {
        id: selectedLesson.id,
        title: selectedLesson.title,
        contentLength: Array.isArray(selectedLesson.content) ? selectedLesson.content.length : 0,
        contentSample: (Array.isArray(selectedLesson.content) && selectedLesson.content.length) ? selectedLesson.content[0] : null,
        rawActivitiesType: rawActivities ? typeof rawActivities : 'none',
        rawActivitiesSample: rawActivities && (Array.isArray(rawActivities) ? rawActivities[0] : (typeof rawActivities === 'string' ? rawActivities.slice(0, 200) : rawActivities))
      });
    } catch (e) { console.error(e); }
  }, [selectedLesson]);

  // ensure selectedWeekIdx is within bounds
  useEffect(() => {
    if (selectedWeekIdx >= weekNumbers.length && weekNumbers.length) {
      setSelectedWeekIdx(0);
    }
  }, [weekNumbers]);

  // Build merged week data for display: pick the week number from weekNumbers[selectedWeekIdx]
  const displayedWeek = (() => {
    if (!selectedLesson) return null;
    const contentWeeks = Array.isArray(selectedLesson.content) ? selectedLesson.content : [];
    const activityWeeks = Array.isArray(selectedLesson.activities) ? selectedLesson.activities : [];
    const actByWeek = {};
    for (const a of activityWeeks) {
      if (a && a.week !== undefined && a.week !== null) actByWeek[String(a.week)] = a;
    }
  const wkNum = weekNumbers && weekNumbers.length ? weekNumbers[selectedWeekIdx] : (selectedWeekIdx + 1);
  if (!wkNum) return null;
  // If the whole lesson is inactive, do not show its weeks
  if (selectedLesson && selectedLesson.status === 'inactive') return null;
    // find base content by explicit week match or by position (index = wkNum-1). Respect status flag.
    let base = (contentWeeks || []).find(c => (c && (c.week !== undefined && c.week !== null) && Number(c.week) === Number(wkNum))) || null;
    if (!base && contentWeeks && contentWeeks.length >= wkNum) base = contentWeeks[wkNum - 1];
    // if base exists and is explicitly inactive, don't show
    if (base) {
      const bstatus = (base.status === undefined || base.status === null) ? 'active' : (base.status || 'active');
      if (bstatus === 'inactive') return null;
    }
    const act = actByWeek[String(wkNum)] || {};
    // if activity exists and is inactive, don't show
    if (act && (act.status !== undefined && act.status === 'inactive')) return null;
    const title = (base && base.title) || '';
    const canva_link = (base && (base.canva_link || base.slide)) || '';
    const summary = (base && base.summary) || '';
    const topic = act.topic || (base && base.topic) || '';
    const items = Array.isArray(act.items) ? act.items : (Array.isArray(base && base.items) ? base.items : ((act && act.items) || (base && base.items) || ''));
    return { week: wkNum, title, canva_link, summary, topic, items };
  })();

  // debug: show resolved lesson/week mapping
  try { console.debug('[LessonLibrary] selectedLesson id', selectedLesson && selectedLesson.id, 'selectedWeekIdx', selectedWeekIdx, 'weekNumbers', weekNumbers, 'displayedWeek', displayedWeek); } catch (e) { }

  // determine if there is any lesson content to show for the current grade/selection
  const noContentForGrade = (!lessonsForGrade || lessonsForGrade.length === 0) || (selectedLesson && (!selectedLesson.content || selectedLesson.content.length === 0));

  // For Students: fetch attendance history per week for student's class
  useEffect(() => {
    let mounted = true;
    async function loadAttendance() {
      try {
        if (!user || (user.role || '').toLowerCase() !== 'student') {
          if (mounted) setAttendanceByWeek({});
          return;
        }
        if (!apiUrl || !user.class_id) { if (mounted) setAttendanceByWeek({}); return; }
        const token = localStorage.getItem('token');
        if (!token) { if (mounted) setAttendanceByWeek({}); return; }
        const res = await fetch(`${apiUrl}/api/classes/${user.class_id}/attendance/weeks`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) { if (mounted) setAttendanceByWeek({}); return; }
        const jd = await res.json();
        const weeks = jd.weeks || [];
        const map = {};
        const exists = {};
        for (const w of weeks) {
          const recs = Array.isArray(w.records) ? w.records : (Array.isArray(w.students) ? w.students : (Array.isArray(w.records || []) ? w.records : []));
          if (!Array.isArray(recs)) continue;
          const me = recs.find(r => String(r.student_id || r.id || r.user_id) === String(user.id));
          if (me) {
            const status = me.status || (me.present ? 'present' : (me.absent ? 'absent' : null));
            // capture optional reason/notes if present on the attendance record
            const reason = me.reason || me.note || me.reason_note || me.absence_reason || null;
            map[String(w.week || w.week_number || w.weekNo || w.weekNo || '')] = { status: status, date: w.date || w.date_recorded || null, reason };
          }
          // mark existence of attendance records for this week (any record -> exists)
          const wkKey = String(w.week || w.week_number || w.weekNo || w.weekNo || '');
          exists[wkKey] = Array.isArray(recs) && recs.length > 0;
        }
        if (mounted) {
          setAttendanceByWeek(map);
          setAttendanceExistsByWeek(exists);
        }
      } catch (e) { console.error('Failed to load attendance for student', e); if (mounted) setAttendanceByWeek({}); }
    }
    loadAttendance();
    return () => { mounted = false; };
  }, [apiUrl, user && user.role, user && user.class_id]);

  // For teachers and mods: fetch attendance existence for the selected grade's class (if found)
  useEffect(() => {
    let mounted = true;
    async function loadForTeacher() {
      try {
        const roleLower = (user && user.role || '').toLowerCase();
        if (!user || (roleLower !== 'teacher' && roleLower !== 'mod')) {
          if (mounted) setAttendanceExistsByWeek(prev => prev || {});
          return;
        }
        if (!apiUrl || !selectedGradeId) return;
        const token = localStorage.getItem('token');
        if (!token) return;
        // find class id: for teacher prefer their class, otherwise pick first class in grade (for mod)
        const cres = await fetch(`${apiUrl}/api/classes`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cres.ok) return;
        const cd = await cres.json();
        const classes = cd.classes || [];
        let classMatch = null;
        if (roleLower === 'teacher') {
          classMatch = classes.find(c => String(c.teacher_username) === String(user.username) && Number(c.grade_id) === Number(selectedGradeId));
        }
        if (!classMatch) {
          classMatch = classes.find(c => Number(c.grade_id) === Number(selectedGradeId));
        }
        if (!classMatch || !classMatch.id) return;
        const classId = classMatch.id;
        const res = await fetch(`${apiUrl}/api/classes/${classId}/attendance/weeks`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const jd = await res.json();
        const weeks = jd.weeks || [];
        const exists = {};
        for (const w of weeks) {
          const recs = Array.isArray(w.records) ? w.records : (Array.isArray(w.students) ? w.students : (Array.isArray(w.records || []) ? w.records : []));
          const wkKey = String(w.week || w.week_number || w.weekNo || w.weekNo || '');
          exists[wkKey] = Array.isArray(recs) && recs.length > 0;
        }
        if (mounted) setAttendanceExistsByWeek(exists);
      } catch (e) { console.error('Failed to load attendance for teacher/mod', e); }
    }
    loadForTeacher();
    return () => { mounted = false; };
  }, [apiUrl, user && user.role, selectedGradeId]);

  // hide selectors for teachers and students: they default to their own grade
  const role = (user && user.role || '').toLowerCase();
  const showSelectors = role !== 'teacher' && role !== 'student';
  const navigate = useNavigate();

  // Small wrapper around react-select to keep using scalar values
  function RSSelect({ options = [], value, onChange, placeholder = '', isClearable = false, emptyValue = '' }) {
    const opts = options.map(o => (o && typeof o === 'object' && o.value !== undefined) ? o : { value: o, label: String(o) });
    const selected = opts.find(o => String(o.value) === String(value)) || null;
    const menuPortalTarget = (typeof document !== 'undefined') ? document.body : undefined;
    const OPTION_HEIGHT = 28;
    const VISIBLE_OPTIONS = 6;
    const MENU_MAX_HEIGHT = OPTION_HEIGHT * VISIBLE_OPTIONS;
    const compactStyles = {
      control: base => ({ ...base, minHeight: 32, height: 32, fontSize: 13, borderRadius: 6, padding: '0 6px', width: 180, maxWidth: '100%' }),
      valueContainer: base => ({ ...base, padding: '0 6px', height: 32 }),
      indicatorsContainer: base => ({ ...base, height: 32 }),
      input: base => ({ ...base, margin: 0, padding: 0 }),
      singleValue: base => ({ ...base, margin: 0 }),
      placeholder: base => ({ ...base, margin: 0, color: '#666', fontSize: 13 }),
      menuPortal: base => ({ ...base, zIndex: 9999 }),
      menu: base => ({ ...base, minWidth: 180, width: 180 }),
      menuList: base => ({ ...base, maxHeight: MENU_MAX_HEIGHT, overflowY: 'auto' }),
      option: base => ({ ...base, fontSize: 13, padding: '4px 8px', minHeight: OPTION_HEIGHT, display: 'flex', alignItems: 'center' })
    };
    return (
      <Select
        options={opts}
        value={selected}
        onChange={opt => onChange(opt ? opt.value : emptyValue)}
        isClearable={isClearable}
        placeholder={placeholder}
        menuPortalTarget={menuPortalTarget}
        menuPosition={'fixed'}
        menuMaxHeight={MENU_MAX_HEIGHT}
        styles={compactStyles}
        components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
        classNamePrefix="rs"
      />
    );
  }

  // Count published weeks for the selected grade.
  // Requirement: count all weeks that have content or activities regardless of status
  // Use a Set of week numbers to avoid double-counting the same week across multiple lessons.
  const totalLessons = 35;
  const publishedSet = new Set();
  for (const l of lessonsForGrade) {
    const contentWeeks = Array.isArray(l.content) ? l.content : [];
    // Activities may provide additional weeks; normalize them too
    const rawActivities = l.activities || l.activity || l.activity_list || l.activity_json || null;
    let activityWeeks = [];
    if (Array.isArray(rawActivities)) activityWeeks = rawActivities;
    else if (typeof rawActivities === 'string' && rawActivities.trim()) {
      try { activityWeeks = JSON.parse(rawActivities); } catch (e) { activityWeeks = []; }
    }

    for (let i = 0; i < contentWeeks.length; i++) {
      const cw = contentWeeks[i];
      // Regardless of status, if a week number is present use it; otherwise fall back to positional index
      const n = (cw && (cw.week !== undefined && cw.week !== null)) ? Number(cw.week) : (i + 1);
      if (n) publishedSet.add(Number(n));
    }

    for (const a of activityWeeks) {
      if (!a) continue;
      // Regardless of status, include activity week number if present
      if (a.week !== undefined && a.week !== null) publishedSet.add(Number(a.week));
    }
  }

  const publishedWeeks = publishedSet.size;
  const lessonsLeft = Math.max(0, totalLessons - publishedWeeks);
  const progressPercent = Math.min(100, Math.round((publishedWeeks / totalLessons) * 100));

  // Dynamically compute week numbers present across lessonsForGrade and classify active vs inactive
  const activeCards = [];
  const inactiveCards = [];
  try {
    const weekMap = new Map(); // wk -> array of occurrences { lesson, kind, item, status }
    for (const l of (lessonsForGrade || [])) {
      const contentWeeks = Array.isArray(l.content) ? l.content : [];
      for (let i = 0; i < contentWeeks.length; i++) {
        const cw = contentWeeks[i];
        const wk = (cw && (cw.week !== undefined && cw.week !== null)) ? Number(cw.week) : (i + 1);
        if (!wk) continue;
        // respect lesson-level status: if lesson is inactive, treat its weeks as inactive
        const itemStatus = (cw && (cw.status === undefined || cw.status === null)) ? 'active' : (cw.status || 'active');
        const status = (l && l.status === 'inactive') ? 'inactive' : itemStatus;
        if (!weekMap.has(wk)) weekMap.set(wk, []);
        weekMap.get(wk).push({ lesson: l, kind: 'content', item: cw, status });
      }
      const rawActivities = l.activities || l.activity || l.activity_list || l.activity_json || null;
      let activityWeeks = [];
      if (Array.isArray(rawActivities)) activityWeeks = rawActivities;
      else if (typeof rawActivities === 'string' && rawActivities.trim()) {
        try { activityWeeks = JSON.parse(rawActivities); } catch (e) { activityWeeks = []; }
      }
      for (const a of (activityWeeks || [])) {
        const wk = (a && (a.week !== undefined && a.week !== null)) ? Number(a.week) : null;
        if (!wk) continue;
        const itemStatus = (a && (a.status === undefined || a.status === null)) ? 'active' : (a.status || 'active');
        const status = (l && l.status === 'inactive') ? 'inactive' : itemStatus;
        if (!weekMap.has(wk)) weekMap.set(wk, []);
        weekMap.get(wk).push({ lesson: l, kind: 'activity', item: a, status });
      }
    }

    const weeks = Array.from(weekMap.keys()).sort((a, b) => a - b);
    for (const wk of weeks) {
      const occ = weekMap.get(wk) || [];
      const activeOcc = occ.find(o => o.status !== 'inactive');
      if (activeOcc) {
        activeCards.push({ wk, lesson: activeOcc.lesson, base: activeOcc.item });
      } else {
        const first = occ[0] || null;
        if (first) inactiveCards.push({ wk, lesson: first.lesson, base: first.item });
      }
    }
  } catch (e) { console.error('Failed to compute week cards', e); }

  // Find active weeks that are missing attendance records (sorted asc by week)
  const activeMissingAttendance = (activeCards || []).filter(a => {
    const wkKey = String(a.wk);
    return !(attendanceExistsByWeek && attendanceExistsByWeek[wkKey]);
  }).sort((x, y) => Number(x.wk) - Number(y.wk));

  // pick only the earliest missing attendance week (if any)
  const earliestMissing = (activeMissingAttendance && activeMissingAttendance.length) ? activeMissingAttendance[0] : null;

  // sorted inactive (finished) lessons: newest lesson id first, then week desc
  const sortedInactiveCards = (inactiveCards || []).slice().sort((a, b) => {
    const aid = Number(a && a.lesson && a.lesson.id) || 0;
    const bid = Number(b && b.lesson && b.lesson.id) || 0;
    if (bid - aid !== 0) return bid - aid;
    const awk = Number(a && a.wk) || 0;
    const bwk = Number(b && b.wk) || 0;
    return bwk - awk;
  });

  // For student mobile view: treat active weeks that already have attendance as finished
  let finalFinishedCards = sortedInactiveCards.slice();
  try {
    if (user && (user.role || '').toLowerCase() === 'student' && isMobile) {
      const extraFromActive = (activeCards || []).filter(a => {
        const wkKey = String(a.wk);
        return !!(attendanceExistsByWeek && attendanceExistsByWeek[wkKey]);
      }).map(a => ({ wk: a.wk, lesson: a.lesson, base: a.base }));
      if (extraFromActive.length) {
        finalFinishedCards = finalFinishedCards.concat(extraFromActive).slice().sort((a, b) => {
          const aid = Number(a && a.lesson && a.lesson.id) || 0;
          const bid = Number(b && b.lesson && b.lesson.id) || 0;
          if (bid - aid !== 0) return bid - aid;
          const awk = Number(a && a.wk) || 0;
          const bwk = Number(b && b.wk) || 0;
          return bwk - awk;
        });
      }
    }
  } catch (e) { /* non-fatal */ }

  return (
    <div className="page-view">
      {!(user && (user.role || '').toLowerCase() === 'student' && isMobile) && (
        <><div className="year-text">Year 2025-2026</div>
        <div className="lesson-progress-bar">
          <div className="lesson-progress-thumbnail">
            {(!isMobile && earliestMissing) ? (
              <div className="missing-attendance-thumbs">
                {(() => {
                  const it = earliestMissing;
                  const l = it.lesson; const base = it.base || {};
                  const thumb = (base && base.thumbnail) || (base && (base.canva_link || base.slide)) || '';
                  const lessonTitle = (base && base.title) || (l && l.title) || `Week ${it.wk}`;
                  return (
                    <button key={`miss-${l ? l.id : 'x'}-${it.wk}`} className="missing-attendance-thumb" onClick={() => l && navigate(`/lessons/${l.id}/week/${it.wk}`)} title={`Week ${it.wk} — ${lessonTitle}`}>
                      {thumb ? (
                        <img src={resolveThumbnailSrc(thumb)} alt={lessonTitle} />
                      ) : (
                        <div className="missing-thumb-placeholder">Week {it.wk}</div>
                      )}
                    </button>
                  );
                })()}
              </div>
            ) : null}
          </div>
          <div className="lesson-progress-content">
            <div className="progress-title">Teaching Progress</div>
            <div className="progress-bar-outer">
              <div className="progress-bar-inner" style={{ width: progressPercent + '%' }}></div>
            </div>
            <div className="progress-label">{lessonsLeft} lessons left</div>
          </div>


        </div></>
      )}
      {showSelectors ? (
        <aside className="lesson-controls">
          <div className="select-grade">
            <label className="label-inline">Grade:</label>
            <div style={{ minWidth: 160 }}>
              <RSSelect
                options={[{ value: '', label: '-- Select Grade --' }].concat((grades || []).map(g => ({ value: g.id, label: g.name })))}
                value={selectedGradeId || ''}
                onChange={v => setSelectedGradeId(v ? Number(v) : null)}
                emptyValue={''}
                isClearable={true}
                placeholder="-- Select Grade --"
              />
            </div>
          </div>
        </aside>
      ) : null}

      <div className="lesson-page">
        {noContentForGrade ? (
          // If teacher/student and they truly have no class assigned, show a clearer message
          ((role === 'teacher' || role === 'student') && hasOwnClass === false) ? (
            <div className="card card-empty">You haven't class yet</div>
          ) : (
            <div className="card card-empty">
              There are no lessons for <strong>{grades.find(g => g.id === selectedGradeId)?.name || 'the school yet, or you haven’t selected a Grade'}</strong>
            </div>
          )
        ) : (role === 'teacher' || role === 'student' || role === 'mod') ? (
          <div>
            <h3 className="title-list-lessons">{(user && (user.role || '').toLowerCase() === 'student' && isMobile) ? "This week's lessons" : 'This month'}</h3>
            <div className="month-grid">
              {(() => {
                let toShow = activeCards;
                if ((user && (user.role || '').toLowerCase() === 'student') && isMobile) {
                  // prefer first active week that has no attendance record for the current student
                  const firstNoAttendance = activeCards.find(a => !attendanceByWeek[String(a.wk)]);
                  toShow = firstNoAttendance ? [firstNoAttendance] : [];
                }
                if (!toShow.length) return <div className="muted">No active lessons this month</div>;
                return toShow.map(item => {
                  const l = item.lesson; const base = item.base || {};
                  // prefer explicit thumbnail stored on the week; fall back to canva link if missing
                  const thumb = (base && (base.thumbnail)) || (base && (base.canva_link || base.slide)) || '';
                  const lessonTitle = (base && base.title) || (l && l.title) || '';
                  return (
                    <div key={`a-${l ? l.id : 'none'}-${item.wk}`} className="month-card" onClick={() => l && navigate(`/lessons/${l.id}/week/${item.wk}`)}>
                      {thumb ? (
                        <div className="month-card-thumb-wrapper">
                          <img src={resolveThumbnailSrc(thumb)} alt={lessonTitle || `Week ${item.wk}`} style={{ width: '100%', height: 180, objectFit: 'cover' }} />
                        </div>
                      ) : (
                        <div className="month-card-placeholder">{lessonTitle || '—'}</div>
                      )}
                      <div className="month-card-info">
                        <div className="month-card-title">{lessonTitle}</div>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>

            <h3 className="title-list-lessons" style={{ marginTop: 50 }}>Finished</h3>
            <div className="month-grid">
              {finalFinishedCards.length ? finalFinishedCards.map(item => {
                const l = item.lesson; const base = item.base || {};
                const lessonTitle = (base && base.title) || (l && l.title) || '';
                // For students on tablet/mobile, show a side-by-side finished card with slide + attendance status
                const studentMobile = (user && (user.role || '').toLowerCase() === 'student' && isMobile);
                if (!l) {
                  return (
                    <div key={`i-empty-${item.wk}`} className={`month-card month-card-inactive ${studentMobile ? 'student-mobile' : ''}`}>
                      <div className="month-card-placeholder">No lessons available</div>
                      <div className="month-card-info">
                        <div className="month-card-meta">No lessons available</div>
                      </div>
                    </div>
                  );
                }
                if (studentMobile) {
                  const thumb = (base && (base.thumbnail)) || (base && (base.canva_link || base.slide)) || '';
                  const info = attendanceByWeek && attendanceByWeek[String(item.wk)] || {};
                  const status = info && info.status ? info.status : null;
                  const dateLabel = info && info.date ? formatDateVNDate(info.date) : '';
                  // build status text and css class
                  const statusText = status === 'present' ? 'Attended' : (status === 'absent' ? 'Absent' : 'No attendance');
                  const statusClass = status === 'absent' ? 'attendance-status absent' : (status === 'present' ? 'attendance-status present' : 'attendance-status unknown');

                  return (
                    <div key={`i-${l.id}-${item.wk}`} className="month-card month-card-inactive student-mobile">
                      {thumb ? (
                        <div className="month-card-thumb">
                          <img src={resolveThumbnailSrc(thumb)} alt={lessonTitle || `Week ${item.wk}`} style={{ width: '100%', height: 120, objectFit: 'cover' }} />
                        </div>
                      ) : (
                        <div className="month-card-thumb month-card-placeholder">{lessonTitle || '—'}</div>
                      )}

                      <div className="month-card-info-right">
                        <div className="month-card-row">
                          <div className="month-card-title">Week {item.wk}</div>
                          {dateLabel ? <div className="month-card-date muted">{dateLabel}</div> : null}
                          <div className={statusClass}>{statusText}{info && info.reason ? ` — ${info.reason}` : ''}</div>
                        </div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={`i-${l.id}-${item.wk}`} className="month-card month-card-inactive non-clickable" aria-disabled="true">
                    {(((base && base.thumbnail) || (base && (base.canva_link || base.slide))) ? (
                      <div className="month-card-iframe-wrapper">
                        {/* render image thumbnail when available, otherwise fallback to iframe for canva links */}
                        {base && base.thumbnail ? (
                          <img src={resolveThumbnailSrc(base.thumbnail)} alt={lessonTitle || `Week ${item.wk}`} style={{ width: '100%', height: 180, objectFit: 'cover' }} />
                        ) : (
                          <iframe allowFullScreen loading="lazy" className="month-card-iframe" width="100%" height={180} src={base.canva_link || base.slide} title={lessonTitle || `Week ${item.wk}`} />
                        )}
                        <div className="iframe-overlay"></div>
                      </div>
                    ) : (
                      <div className="month-card-placeholder">Bài giảng đã được tắt hoặc đã được hoàn thành</div>
                    ))}
                    <div className="month-card-info">
                      <div className="month-card-title">{lessonTitle || '—'}</div>
                    </div>
                  </div>
                );
              }) : <div className="muted">No lessons completed</div>}
            </div>


          </div>
        ) : (
          <main className="lesson-main three-card">
            <div className="card card-weeks">
              <h4>Week</h4>
              <div className="week-buttons">
                {weekNumbers.map((wk, idx) => (
                  <button key={wk} className={idx === selectedWeekIdx ? 'date active' : 'date'} onClick={() => setSelectedWeekIdx(idx)}>
                    Week {wk}
                  </button>
                ))}
              </div>
            </div>

            <div className="card card-title">
              {displayedWeek ? (
                <div>
                  <span className="lesson-title">{displayedWeek.title || '—'}</span>
                  {user && user.role && user.role.toLowerCase() === 'student' && (
                    <div className="attendance-info" style={{ marginTop: 8 }}>
                      {(() => {
                        const wkNum = displayedWeek.week;
                        const info = attendanceByWeek && attendanceByWeek[String(wkNum)];
                        if (!info) return <div className="muted">No attendance information for this week</div>;
                        const label = info.status === 'present' ? 'Present' : (info.status === 'absent' ? 'Absent' : 'Unknown');
                        return <div>Attendance: <strong>{label}</strong> &nbsp; {info.date ? <span className="muted">({formatDateVNDate(info.date)})</span> : null}</div>;
                      })()}
                    </div>
                  )}
                  {displayedWeek.canva_link && (
                    <iframe
                      src={displayedWeek.canva_link}
                      allowFullScreen="allowFullScreen"
                      loading="lazy"
                      className="lesson-iframe"
                    />
                  )}
                </div>
              ) : (
                <div>No content available</div>
              )}
            </div>

            <div className="card card-summary">
              <h4>Summary</h4>
              {displayedWeek ? (
                <div>
                  {(() => {
                    const s = displayedWeek.summary;
                    if (!s) return <span>—</span>;
                    let items = [];
                    if (Array.isArray(s)) items = s;
                    else if (typeof s === 'string') items = s.split(',').map(ss => ss.trim()).filter(Boolean);
                    if (!items.length) return <span>—</span>;
                    return <ul className="summary-list">{items.map((it, i) => <li key={i}>{it}</li>)}</ul>;
                  })()}
                </div>
              ) : (
                <div>No summary available</div>
              )}
              {/* Activities: show topic and items under summary */}
              {displayedWeek && (
                <div className="card-activities">
                  <h4>Activities</h4>
                  {(() => {
                    const topic = displayedWeek.topic || '';
                    const itemsRaw = displayedWeek.items || [];
                    let items = [];
                    if (Array.isArray(itemsRaw)) items = itemsRaw;
                    else if (typeof itemsRaw === 'string') items = itemsRaw.split(',').map(x => x.trim()).filter(Boolean);
                    return (
                      <div>
                        <div className="act-topic-panel">Topic:
                          <div className="topic-list">
                            {topic || <span>No information available</span>}
                          </div>
                        </div>
                        <div className="act-item-panel">Items:
                          {items.length ? <ul className="act-item-list">{items.map((it, i) => <li key={i}>{it}</li>)}</ul> : <div className="muted">No activities available</div>}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </main>
        )}
      </div>
    </div>
  );
}
